import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Observable, of, Subscription } from 'rxjs';
import { ViewVoteIconsConstants } from '../../../../../../shared/constants/view-vote-icons-constants';
import { IApiResponse } from '../../../../../../shared/models/api-response';
import { ToastService } from '../../../../../../shared/services/toast.service';
import { ViewVoteDocumentsPopoverComponent } from '../../../../shared/components/view-vote-documents-popover/view-vote-documents-popover.component';
import { ViewVotesPopoverComponent } from '../../../../shared/components/view-votes-popover/view-votes-popover.component';
import { ISrsBallotVote, SrsVoteId, srsVoteOption, ISrsBallotVoteComment } from '../../../../shared/models/sr/srs-vote';
import { IVoteDocument } from '../../../../shared/models/vote-document';
import { IVotePopoverItem } from '../../../../shared/models/vote-popover-item';
import { BallotUri, BallotDocumentService } from '../../../../shared/services/ballot-document.service';
import { SrsBallotService } from '../../../../shared/services/srs-ballot.service';


@Component({
    selector: 'app-srs-vote-column',
    templateUrl: './srs-vote-column.component.html',
    styleUrl: './srs-vote-column.component.scss',
    standalone: false
})
export class SrsVoteColumnComponent implements OnChanges, OnDestroy {
  @ViewChild('commentPopover') commentPopover!: ViewVotesPopoverComponent;
  @ViewChild('historyPopover') historyPopover!: ViewVotesPopoverComponent;
  @ViewChild('authorisationDocumentsPopover') authorisationDocumentsPopover!: ViewVoteDocumentsPopoverComponent;
  @Input() vote: ISrsBallotVote | null = null;
  @Input() isUserPmOrAboveOrHoSD: boolean = false;
  @Input() isUserCM: boolean = false;
  @Input() loginUserEmail: string = '';

  subscriptions: Subscription = new Subscription();
  voteCommentPopoverItems: IVotePopoverItem[] = [];
  voteHistoryPopoverItems: IVotePopoverItem[] = [];

  public readonly ViewVoteIconsConstants = ViewVoteIconsConstants;

  iconName: string = '';
  colorClass: string = '';
  hasComment: boolean = false;
  hasSupportingInformationDocument: boolean = false;
  hasAuthorisationDocument: boolean = false;
  hasHistories: boolean = false;
  showAuthorisationDocumentIcon: boolean = false;

  BallotType = BallotUri.SR;

  constructor(
    private _srsBallotService: SrsBallotService,
    private _ballotDocumentService: BallotDocumentService,
    private _toastService: ToastService,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['vote']) {
      this.vote = changes['vote'].currentValue;
      if (this.vote) {
        const voteId = this.vote?.voteTypeId;
        const isEligible = this.vote?.isEligible;
        if (isEligible) {
          this.checkVoteType(voteId as SrsVoteId);
          this.hasComment = this.checkComment(this.vote);
          this.hasSupportingInformationDocument = this.vote.hasSupportingInformationDocument;
          this.checkAuthorisationDocument(this.vote);
          this.checkHistories(this.vote);
          this.checkUserVote(this.vote);
        }
      }
    }
  }

  checkVoteType(voteId: SrsVoteId): void {
    this.iconName = srsVoteOption[voteId]?.icon;
    this.colorClass = srsVoteOption[voteId]?.color;
  }

  checkComment(vote: ISrsBallotVote): boolean {
    if (!vote?.comments?.length) {
      return false; // No comments at all
    }

    return vote.comments.some(
      (c) => c?.comment != null && c.comment.trim() !== ''
    );
  }

  checkAuthorisationDocument(vote: ISrsBallotVote): void {
    this.hasAuthorisationDocument = vote.hasAuthorisationDocument;
  }

  checkHistories(vote: ISrsBallotVote): void {
    if (vote.submitted && vote.submitted.toString().trim() !== '') {
      this.hasHistories = true;
    } else {
      this.hasHistories = false;
    }
  }

  checkUserVote(vote: ISrsBallotVote) {
    if (this.isUserPmOrAboveOrHoSD) {
      this.showAuthorisationDocumentIcon = this.hasAuthorisationDocument;
    } else if (this.isUserCM) {
      const voterEmail = vote.email;
      if (voterEmail?.trim() && this.loginUserEmail?.trim()) {
        this.showAuthorisationDocumentIcon =
          voterEmail.toLowerCase() == this.loginUserEmail?.toLowerCase() &&
          this.hasAuthorisationDocument;
      } else {
        this.showAuthorisationDocumentIcon = false;
      }
    }
  }

  openPopover(popover: ViewVotesPopoverComponent) {
    this.getVotePopoverItems();
    popover.openPopover();
  }

  openSupportingInformationDocumentsPopover(popover: ViewVoteDocumentsPopoverComponent) {
    this.getSupportingInformationDocument()
    .subscribe({
      next: (response) => {
        if (response && response.success) {
          //popover.voteDocument = response.content;
          popover.openPopover(response.content);
        } else {
          this._toastService.showError("Download failed for this document.", "Download Failed");
        }
      },
      error: (error) => {
        this._toastService.showError("Download failed for this document.", "Download Failed");
      },
    });

  }

  openAuthorisationDocumentsPopover(popover: ViewVoteDocumentsPopoverComponent) {
    this.getAuthorisationDocument()
    .subscribe({
      next: (response) => {
        if (response && response.success) {
          //popover.voteDocument = response.content;
          popover.openPopover(response.content);
        } else {
          this._toastService.showError("Download failed for this document.", "Download Failed");
        }
      },
      error: (error) => {
        this._toastService.showError("Download failed for this document.", "Download Failed");
      },
    });

  }

  getVotePopoverItems(): void {
    this.voteCommentPopoverItems = [];
    this.voteHistoryPopoverItems = [];

    if (this.vote?.comments && this.vote.comments.length > 0) {
      this.subscriptions.add(
        this._srsBallotService
          .getSrsBallotVoteHistory(this.vote.ballotId, this.vote.id)
          .subscribe({
            next: (response) => {
              if (response && response.success) {
                this.voteCommentPopoverItems = response.content.comments
                  .filter(
                    (comment: ISrsBallotVoteComment) => srsVoteOption[comment.voteResult] !== undefined && comment.comment !== null && comment.comment?.trim() !== ''
                  )
                  .map((comment: ISrsBallotVoteComment) => {
                    const voteResult = srsVoteOption[comment.voteResult];
                    const popoverItem: IVotePopoverItem = {
                      voteOption: voteResult,
                      additionalComments: comment.comment,
                      voteSubmittedDate: comment.submitted,
                    };

                    return popoverItem;
                  });

                this.voteHistoryPopoverItems = response.content.comments
                  .filter(
                    (comment: ISrsBallotVoteComment) => srsVoteOption[comment.voteResult] !== undefined
                  )
                  .map((comment: ISrsBallotVoteComment) => {
                    const voteResult = srsVoteOption[comment.voteResult];
                    const leadProjectPropoalQuestion =
                      'Would you be willing to lead the project proposal development: ';
                    const leadProjectProposalAnswer =
                      comment.leadProjectProposal === true ? 'Yes' : 'No';
                    const popoverItem: IVotePopoverItem = {
                      voteOption: voteResult,
                      voteSubmittedDate: comment.submitted,
                      additionalComments:
                        comment.voteResult === SrsVoteId.SVT_REVISE &&
                        comment.leadProjectProposal === true
                          ? leadProjectPropoalQuestion +
                            leadProjectProposalAnswer
                          : null,
                    };

                    return popoverItem;
                  });
              } else {
                console.error('Error fetching vote history.', response.message);
              }
            },
            error: (error) => {
              console.error('Error fetching vote history.', error);
            },
          })
      );
    }
  }

  getAuthorisationDocument(): Observable<IApiResponse<IVoteDocument> | null> {
    if (this.vote && this.hasAuthorisationDocument) {
      return this._ballotDocumentService.DownloadAuthDoc(BallotUri.SR, this.vote.id, this.vote.ballotId);
    }
    return of(null);
  }

  getSupportingInformationDocument(): Observable<IApiResponse<IVoteDocument> | null> {
    if (this.vote && this.hasSupportingInformationDocument) {
      return this._ballotDocumentService.DownloadSupportingInformationDocument(BallotUri.SR, this.vote.id, this.vote.ballotId);
    }
    return of(null);
  }
}
